'use client';
import styled, { css } from 'styled-components';
import Button from 'antd/es/button';

import theme from '@theme';

import { ButtonProps, ButtonType } from './ButtonInterface';

const BUTTON_SIZE = {
  small: css`
    padding: 8px 16px;
    font-size: 12px;
    line-height: 16px;
    min-width: 76px;
    height: 32px;
    font-weight: 600;
  `,
  medium: css`
    padding: 6px 16px;
    font-size: 14px;
    line-height: 24px;
    min-width: 83px;
    height: 36px;
    font-weight: 600;
  `,
  large: css`
    padding: 8px 16px;
    font-size: 14px;
    line-height: 24px;
    min-width: 83px;
    height: 40px;
    font-weight: 600;
  `,
  xlarge: css`
    padding: 16px;
    font-size: 14px;
    line-height: 24px;
    min-width: 83px;
    height: 56px;
    font-weight: 600;
  `,
};

export const ButtonTypeMixin = ({
  backgroundColor,
  color,
  borderColor,
  borderWidth = '1px',
  isOutline,
}: {
  backgroundColor: string;
  color: string;
  borderColor?: string;
  borderWidth?: string;
  isOutline?: boolean;
}) => {
  return css`
    :hover {
      background-color: ${backgroundColor} !important;
      color: ${color} !important;
      border-color: ${({ theme }) =>
        isOutline ? theme.palette.grey1100 : borderColor};
    }
    :focus {
      background-color: ${({ theme }) =>
        isOutline ? theme.palette.grey100 : backgroundColor};
      color: ${color};
      border-color: ${borderColor};
    }
    :active {
      box-shadow: none !important;
      color: ${color};
      border-color: ${({ theme }) =>
        isOutline ? theme.palette.grey1100 : borderColor};
    }

    background-color: ${backgroundColor};
    color: ${color};
    border-color: ${borderColor};
    border-width: ${borderWidth};
  `;
};

const OutlineDisabledButtonMixin = ({
  backgroundColor,
  color,
  borderColor,
  borderWidth,
}: {
  backgroundColor: string;
  color: string;
  borderColor?: string;
  borderWidth?: string;
}) => {
  return css`
    background-color: ${backgroundColor} !important;
    color: ${color} !important;
    border-color: ${borderColor} !important;
    border-width: ${borderWidth} !important;
    opacity: 0.5;
  `;
};

export const LinkButtonMixin = css`
  background-color: transparent !important;
  border: 0;
  color: ${({ theme }) => theme.palette.grey800};
  :hover {
    color: ${({ theme }) => theme.palette.grey1100} !important;
    background-color: transparent !important;
    border: none;
  }
  :active {
    color: ${({ theme }) => theme.palette.grey1000} !important;
    background-color: transparent !important;
    border: none;
  }
`;

const MouseActionColorMixin = (type: ButtonType) => {
  const MouseAction = {
    primary: css`
      :hover {
        color: ${({ theme }) => theme.palette.yellow500} !important;
        background-color: ${({ theme }) =>
          theme.palette.primaryBrand} !important;
      }
      :active {
        color: ${({ theme }) => theme.palette.yellow500} !important;
        background-color: ${({ theme }) =>
          theme.palette.primaryBrand} !important;
      }
    `,
    ghost: css`
      :hover {
        color: ${({ theme }) => theme.palette.grey1100} !important;
        background-color: ${({ theme }) => theme.palette.grey300} !important;
      }
      :active {
        color: ${({ theme }) => theme.palette.grey1100} !important;
        background-color: ${({ theme }) => theme.palette.grey400} !important;
      }
    `,
    link: css`
      :hover {
        color: ${({ theme }) => theme.palette.grey1100} !important;
        background-color: transparent;
        border: none;
      }
      :active {
        color: ${({ theme }) => theme.palette.grey1000} !important;
        background-color: transparent !important;
        border: none;
      }
    `,
  };

  return MouseAction[type];
};

export const ButtonMixin = ({
  disabled,
  outline,
  color,
  backgroundColor,
  type,
}: {
  disabled?: boolean;
  outline?: boolean;
  color: string;
  backgroundColor: string;
  boxShadow?: string;
  type?: ButtonType;
}) => {
  if (outline) {
    return !disabled
      ? css`
          ${ButtonTypeMixin({
            backgroundColor: color,
            color: backgroundColor,
            borderColor: theme.palette.grey300,
            borderWidth: '1px',
            isOutline: true,
          })}
        `
      : css`
          ${OutlineDisabledButtonMixin({
            backgroundColor: color,
            color: backgroundColor,
            borderColor: theme.palette.grey300,
          })}
        `;
  } else {
    return !disabled
      ? css`
          ${ButtonTypeMixin({
            backgroundColor: backgroundColor,
            color: color,
            borderColor: backgroundColor,
            borderWidth: '1px',
          })}

          ${MouseActionColorMixin(type || 'primary')}
        `
      : css`
          background-color: ${backgroundColor} !important;
          color: ${color} !important;
          opacity: 0.5;
        `;
  }
};

const StyledButton = styled(
  ({
    outline = false,
    size = 'large',
    type = 'primary',
    disabled = false,
    ...otherButtonProps
  }) => (
    <Button
      outline={outline}
      size={size}
      type={type}
      disabled={disabled}
      {...otherButtonProps}
    />
  )
)<ButtonProps>`
  &&& {
    :after {
      -webkit-animation: none !important;
      -moz-animation: none !important;
      -o-animation: none !important;
      -ms-animation: none !important;
      animation: none !important;
    }
    ${({
      type,
      theme,
      disabled,
      outline,
      customBackgroundColor,
      customColor,
    }) =>
      type === 'primary'
        ? css`
            ${ButtonMixin({
              disabled,
              outline,
              color: customColor ?? theme.palette.white,
              backgroundColor:
                customBackgroundColor ?? theme.palette.primaryBrand,
              type: type,
            })}
          `
        : css`
            ${ButtonMixin({
              disabled,
              outline,
              color: theme.palette.grey1100,
              backgroundColor: theme.palette.grey200,
              type: type,
            })}
          `}
    box-shadow: none;
    border-radius: 4px;

    ${({ size }) => BUTTON_SIZE[size || 'large']}
    ${({ type }) => type === 'link' && LinkButtonMixin}
    ${({ fullWidth }) =>
      fullWidth &&
      css`
        width: 100%;
      `}
  }
`;

export default StyledButton;
